import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { List } from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";

import { IconButton } from "../redesign/components/IconButton";
import { StickyScrollPageHeader } from "../redesign/components/StickyScrollPageHeader";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "../redesign/Shift/constants";
import { ShiftDurationListItem } from "../redesign/Shift/DurationListItem";
import { useShiftDurationWithBreak } from "../redesign/Shift/hooks/useShiftDurationWithBreak";
import { type ModalShiftData } from "../redesign/Shift/types";
import { ListItemPay } from "./components/ListItemPay";
import { ListItemRateNegotiationHistory } from "./components/ListItemRateNegotiationHistory";
import { ListItemShiftDate } from "./components/ListItemShiftDate";
import { ListItemWorkplace } from "./components/ListItemWorkplace";

interface RateNegotiationDialogContentProps {
  shift: ModalShiftData;
  openRateNegotiationSupportLink: () => Promise<void>;
}

export function RateNegotiationDialogContent(props: RateNegotiationDialogContentProps) {
  const { shift, openRateNegotiationSupportLink } = props;
  const { workplace, attributes: shiftAttributes, offer, rateNegotiation } = shift;
  const rateNegotiationHistory = rateNegotiation?.history ?? [];

  // Get shift duration with break information
  const { totalDurationInHours, mandatoryBreakDurationInMinutes } = useShiftDurationWithBreak({
    startDate: isDefined(shiftAttributes) ? parseISO(shiftAttributes.start) : new Date(),
    endDate: isDefined(shiftAttributes) ? parseISO(shiftAttributes.end) : new Date(),
    workplaceId: workplace.id,
    requiresLunchBreak: workplace.attributes.requiresLunchBreak,
  });

  // Calculate if shift requires a break based on total duration
  const facilityRequiresBreak = workplace.attributes.requiresLunchBreak ?? true;
  const isShiftLongerThanMinimumTimeForMandatoryBreaks =
    totalDurationInHours >= MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS;
  const requiresBreak = isShiftLongerThanMinimumTimeForMandatoryBreaks && facilityRequiresBreak;

  return (
    <DialogContent sx={{ backgroundColor: "unset" }}>
      <StickyScrollPageHeader
        title="Shift Rate Negotiation"
        headerVariant="tertiary"
        rightAction={
          <IconButton
            iconType="help-outlined"
            size="medium"
            variant="outlined"
            onClick={async () => {
              await openRateNegotiationSupportLink();
            }}
          />
        }
      />
      <Stack alignItems="left" flex={1}>
        <List>
          <ListItemShiftDate
            shiftStartDate={shiftAttributes.start}
            workplaceTimeZone={workplace.attributes.location.timezone}
          />
          <ShiftDurationListItem
            divider
            shiftTimeSlot={shiftAttributes.timeSlot}
            shiftStartIso={shiftAttributes.start}
            shiftEndIso={shiftAttributes.end}
            shiftDurationInHours={totalDurationInHours}
            breakDurationInMinutes={mandatoryBreakDurationInMinutes}
            requiresBreak={requiresBreak}
            workplaceTimeZone={workplace.attributes.location.timezone}
            minutesElapsed={0}
          />

          <ListItemWorkplace
            workplaceId={workplace.id}
            workplaceName={workplace.attributes.name}
            workplaceType={workplace.attributes.type ?? ""}
            workplaceLocation={workplace.attributes.location}
          />

          {isDefined(offer) && <ListItemPay offer={offer} />}
          {rateNegotiationHistory.map((history) => (
            <ListItemRateNegotiationHistory key={history.createdAt} history={history} />
          ))}
        </List>
      </Stack>
    </DialogContent>
  );
}
